import { useCallback, useMemo } from 'react';
import { generatePath, useSearchParams } from 'react-router-dom';
import { PAGE_PARAM, ReportItemsCountsAppModel, StandingAccessReportAppModel } from '@api';
import useGetReport from '@StandingAccess/hooks/use-get-report';
import {
  ASSESSMENT_REPORT_TIER_FILTER,
  isReportTierOptions,
  ReportTierOptions,
  REPORT_TIER_TO_REMEDIATE_PARAM,
  TIER_DESCRIPTION,
} from '@StandingAccess/types';
import { Button, Grid, Stack } from '@mui/material';
import AssessmentReportItemRemediation from '@StandingAccess/components/AssessmentReportItemRemediation';
import AssessmentTierWidget from '@StandingAccess/components/AssessmentTierWidget';
import { getTierSummary } from '@StandingAccess/hooks/use-report-score';
import { useFlagFixed } from '@hooks';
import { Flag } from '@utils';
import { MaterialIcon } from '@components';
import { RouterLink } from '@libs/router';
import { routes } from '@routes';

export default function AssessmentReportWidgets({ reportId }: { reportId: string }) {
  const { report } = useGetReport(reportId);
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTiers = useMemo(
    () => searchParams.getAll(ASSESSMENT_REPORT_TIER_FILTER.param).filter(isReportTierOptions),
    [searchParams],
  );

  const onActiveTierChange = useCallback(
    (tier: ReportTierOptions) => {
      searchParams.delete(PAGE_PARAM);
      const currentTiers = searchParams.getAll(ASSESSMENT_REPORT_TIER_FILTER.param);
      const newTiers = currentTiers.includes(tier) ? currentTiers.filter((t) => t !== tier) : [...currentTiers, tier];
      searchParams.delete(ASSESSMENT_REPORT_TIER_FILTER.param);
      newTiers.forEach((t) => searchParams.append(ASSESSMENT_REPORT_TIER_FILTER.param, t));
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const onRemediateClick = useCallback(
    (tier: ReportTierOptions) => {
      searchParams.set(REPORT_TIER_TO_REMEDIATE_PARAM, tier);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  if (!report) return null;

  return (
    <div>
      <Grid container spacing={2}>
        {Object.values(ReportTierOptions).map((tier) => (
          <Grid item xs={3} key={tier}>
            <TierWidget
              report={report.report}
              tier={tier}
              active={activeTiers.includes(tier)}
              onClick={onActiveTierChange}
              onRemediate={onRemediateClick}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );
}

function TierWidget({
  tier,
  report,
  active,
  onClick,
  onRemediate,
}: {
  tier: ReportTierOptions;
  report: StandingAccessReportAppModel;
  active: boolean;
  onClick: (tier: ReportTierOptions) => void;
  onRemediate: (tier: ReportTierOptions) => void;
}) {
  const tierSummary = getTierSummary(report, tier);

  return (
    <AssessmentTierWidget
      type={tier}
      title={`${tierSummary.entitlements} Entitlements`}
      subTitle={`${tierSummary.identities} Identities`}
      description={TIER_DESCRIPTION[tier]}
      active={active}
      onClick={() => onClick(tier)}
    >
      <TierActions
        tierSummary={tierSummary}
        onRemediate={() => onRemediate(tier)}
        revokePath={`${generatePath(routes.StandingAccessReportRevoke.path, { id: report.id })}?tier=${tier}`}
      />
    </AssessmentTierWidget>
  );
}

function TierActions({
  tierSummary,
  onRemediate,
  revokePath,
}: {
  tierSummary: ReportItemsCountsAppModel;
  onRemediate: () => void;
  revokePath: string;
}) {
  const { isEnabled: isAccessFlowEnabled } = useFlagFixed(Flag.ASSESSMENT_REPORTS_ACCESS_FLOW);

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onRemediate();
  };

  const isDisabled = tierSummary.iras - tierSummary.iras_revoked === 0;

  const action = isAccessFlowEnabled ? (
    <Button variant="outlined" color="secondary" onClick={handleButtonClick}>
      Remediation Plan
    </Button>
  ) : (
    <Button
      variant="outlined"
      color="secondary"
      endIcon={isDisabled ? undefined : <MaterialIcon symbol="arrow_right_alt" />}
      component={RouterLink}
      to={revokePath}
      disabled={isDisabled}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {isDisabled ? 'All assignments are revoked' : 'Revoke standing access'}
    </Button>
  );

  return (
    <Stack direction="column" spacing={2}>
      <AssessmentReportItemRemediation
        inAccessFlow={isAccessFlowEnabled ? tierSummary.iras_in_access_flow : undefined}
        revoked={tierSummary.iras_revoked}
        total={tierSummary.iras}
      />
      {action}
    </Stack>
  );
}
